/**
 * **Adjust** — `docs/visit-page.md` §16.5 (Board 12 C's lock banner, Board 13
 * A). After close a change is an adjustment with a reason — never an edit,
 * never a reopen (do-not-substitute 12, D15).
 *
 * No board draws the popup; it is built as Board 11 draws its popups (470,
 * the 34px tile, the 14.5/700 title; a phone's sheet from the bottom) and
 * says only the boards' words (lead, round 2 item 10): *Category* (§6's,
 * as chips — the green selected chip, never black), *Amount* (signed), *Why*
 * (required). `POST /visits/:id/adjustments {category, amount, reason}` →
 * a `cost_ledger` row `kind = 'adjustment'`, the job recomputed; it lists on
 * Money under the table, and in History.
 */
import { useId, useState } from 'react'
import { COST_CATEGORIES, adjustmentSchema, type CostCategory, type VisitRead, type VisitTabKey } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useVisitWrite, type VisitWriteResult } from './api.js'
import { VisitGlyph } from './glyphs.js'
import { VisitButton, VisitPopup, VisitTile, VISIT_LAYER } from './sheet.js'
import { FIELD_BOX, FIELD_INPUT, FieldLabel } from './fields.js'
import { ADJUST } from './copy.js'

/** *Subcontractors* is offered only where the visit has sub lines (§6.1) — never on an adjustment's first pick. */
const OFFERED: readonly CostCategory[] = COST_CATEGORIES.filter((key) => key !== 'subs')

export function AdjustPopup({ visitId, read, tab = 'overview', phone: forced, layer = VISIT_LAYER, onClose, onAdjusted }: {
  visitId: string
  read: VisitRead
  /** The tab the banner was pressed on, whose fresh read comes back (Money lists the adjustments). */
  tab?: VisitTabKey
  phone?: boolean
  layer?: number
  onClose: () => void
  /** After **Adjust** — with the answer, so the tab it came from can take its fresh read. */
  onAdjusted?: (result: VisitWriteResult) => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const write = useVisitWrite(visitId)
  const [category, setCategory] = useState<CostCategory | null>(null)
  const [amount, setAmount] = useState('')
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const amountId = useId()
  const whyId = useId()

  /* The schema the server parses with decides when **Adjust** can be pressed — the page checks no figure itself. */
  const body = { category, amount: amount.trim().replace(/^−/, '-').replace(/^\+/, ''), reason: why.trim() }
  const valid = adjustmentSchema.safeParse(body).success
  const ready = valid && !busy

  async function adjust() {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const result = await write('POST', '/adjustments', body, { tab })
      onAdjusted?.(result)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={ADJUST.title}
      sub={read.reference}
      mark={<VisitTile glyph="pencil" tone="grey" size={34} />}
      onClose={onClose}
      layer={layer}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{ADJUST.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" disabled={!ready} onClick={() => void adjust()}>{ADJUST.submit}</VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <FieldLabel required>{ADJUST.category}</FieldLabel>
          <div role="radiogroup" aria-label={ADJUST.category} className="flex flex-wrap gap-1.5">
            {OFFERED.map((key) => {
              const on = key === category
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setCategory(key)}
                  className={cn('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold',
                    phone ? 'h-11 px-3.5 text-[13px]' : 'h-[30px] px-2.5 text-[12px]',
                    on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover')}
                >
                  {on && <VisitGlyph name="check" size={12} stroke={2.6} />}
                  {ADJUST.categories[key]}
                </button>
              )
            })}
          </div>
        </div>

        <div className="min-w-0">
          <FieldLabel required htmlFor={amountId}>{ADJUST.amount}</FieldLabel>
          <div className={FIELD_BOX}>
            <input
              id={amountId}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className={cn(FIELD_INPUT, 'tabular-nums')}
            />
          </div>
        </div>

        <div className="min-w-0">
          <FieldLabel required htmlFor={whyId}>{ADJUST.why}</FieldLabel>
          <textarea
            id={whyId}
            value={why}
            onChange={(event) => setWhy(event.target.value)}
            className="block h-14 w-full resize-none tabular-nums rounded-button border border-line bg-surface px-3 py-2.5 text-[13px]
                       leading-normal text-text focus:border-accent-line focus:outline-none"
          />
        </div>

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}
